// src/App.js
import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import usePageViewTracking from "./hooks/userPageViewTracking";
import Dashboard from "./components/Dashboard";
import Login from "./components/account/Login";
import ForgotPassword from "./components/account/ForgotPassword";

// Client view components
import ClientListView from "./components/ClientListView";
import DashboardOverview from "./components/DashboardOverview";
import GoalListView from "./components/GoalListView";
import IssueListView from "./components/IssueListView";
import ActionItemListView from "./components/ActionItemListView";
import SentimentListView from "./components/SentimentListView";
import KnowledgeGraphView from "./components/KnowledgeGraphView";
import InsightsView from "./components/InsightsView";
import EntityDetailPage from "./components/EntityDetailPage";

// Staff view components
import StaffListView from "./components/StaffListView";
import StaffView from "./components/StaffView";

// Internal/Department View Components
import DepartmentListView from "./components/DepartmentListView";
import InternalOverviewView from "./components/InternalOverviewView";
import InternalGoalListView from "./components/InternalGoalListView";
import InternalActionItemListView from "./components/InternalActionItemListView";
import InternalSentimentView from "./components/InternalSentimentView";
import InternalIssueListView from "./components/InternalIssueListView"; // <-- IMPORTED
import { isAdminUser as isAdmin } from "./utils";
import ResetPassword from "./components/account/ResetPassword";
import UpdatePassword from "./components/account/UpdatePassword";

const NotFound = () => (
  <div className="text-center p-10">
    <h2 className="text-xl font-semibold mb-6">404 - Not Found</h2>
    <p className="text-gray-500">The page you requested does not exist.</p>
  </div>
);

function App() {
  // google analytics
  usePageViewTracking();

  const [isAdminUser, setIsAdminUser] = useState(false)
  useEffect(() => {
    try {
      const setAdminView = async () => {
        const admin = await isAdmin();
        setIsAdminUser(() => admin);
      }
      setAdminView();
    } catch(e) {
      console.log(e)
    }
  }, [])

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<Login />} />
        
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/update-password" element={<UpdatePassword />} />

        <Route path="/clients" element={<Dashboard />}>
          <Route index element={<ClientListView />} />
          <Route path=":clientId">
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<DashboardOverview />} />
            <Route path="goals" element={<GoalListView />} />
            <Route path="issues" element={<IssueListView />} />
            <Route path="action-items" element={<ActionItemListView />} />
            <Route path="sentiment" element={<SentimentListView />} />
            <Route path="graph" element={<KnowledgeGraphView />} />
            <Route path="insights" element={<InsightsView />} />
            {/* Generic entity detail page for clients */}
            <Route
              path="goals/:entityId"
              element={<EntityDetailPage entityType="goals" />}
            />
            <Route
              path="initiatives/:entityId"
              element={<EntityDetailPage entityType="initiatives" />}
            />
            <Route
              path="issues/:entityId"
              element={<EntityDetailPage entityType="issues" />}
            />
            <Route
              path="action-items/:entityId"
              element={<EntityDetailPage entityType="action-items" />}
            />
            {/* Fallback for any other sub-path under a client to its overview */}
            <Route path="*" element={<Navigate to="overview" replace />} />
          </Route>
        </Route>

        <Route path="/staff" element={<Dashboard />}>
          <Route index element={<StaffListView />} />
          <Route path=":staffId" element={<StaffView />} />
          <Route path="*" element={<Navigate to="/staff" replace />} />
        </Route>

        {isAdminUser ? (
          <Route path="/internal" element={<Dashboard />}>
            <Route index element={<DepartmentListView />} />
            <Route path=":departmentId">
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<InternalOverviewView />} />
              <Route path="goals" element={<InternalGoalListView />} />
              <Route
                path="action-items"
                element={<InternalActionItemListView />}
              />
              <Route path="issues" element={<InternalIssueListView />} />{" "}
              {/* <-- ADDED ROUTE */}
              <Route path="sentiment" element={<InternalSentimentView />} />
              {/* Department-specific entity detail pages - adjust entityType prop as needed or make EntityDetailPage context-aware */}
              <Route
                path="goals/:entityId"
                element={<EntityDetailPage entityType="goals" />}
              />
              <Route
                path="initiatives/:entityId"
                element={<EntityDetailPage entityType="initiatives" />}
              />
              <Route
                path="issues/:entityId"
                element={<EntityDetailPage entityType="issues" />}
              />
              <Route
                path="action-items/:entityId"
                element={<EntityDetailPage entityType="action-items" />}
              />
              <Route path="*" element={<Navigate to="overview" replace />} />
            </Route>
          </Route>
        ) : null}

        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
}

export default App;
